import { useEffect, useState } from "react";
import { api, fmtDate, fmtMoney, formatErr } from "../lib/api";
import { Drawer, Field, StatusBadge } from "../components/UI";
import { Page, useBrand } from "../components/AppShell";
import ListControls, { STATUS_OPTS } from "../components/ListControls";
import { Plus, Trash, PaperPlaneTilt, CreditCard, DownloadSimple } from "@phosphor-icons/react";
import CustomerFormDrawer from "../components/CustomerFormDrawer";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Invoices() {
  const { platformPossessive } = useBrand();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, page_size: 20 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", due_date: "", tax: "0", notes: "", lines: [{ description: "", quantity: "1", unit_price: "0" }] });
  const [paymentStatus, setPaymentStatus] = useState("");
  const [stripeReady, setStripeReady] = useState(true); // true while loading to avoid flicker; recomputed below
  const [statements, setStatements] = useState([]);
  // iter 34 Phase E — Payments tab (received checks + card payments).
  const [payments, setPayments] = useState([]);
  // iter 32 follow-up: tabbed view — only one table visible at a time.
  // "invoices" (default), "statements", or "payments".
  const [view, setView] = useState("invoices");
  // Drawer stacking: open a child drawer to create a new customer from inside
  // the New Invoice drawer. On save, the new customer is auto-selected.
  const [addCustOpen, setAddCustOpen] = useState(false);

  const onCustomerCreated = async (created) => {
    // Refresh customer list and pre-select the new one in the parent invoice form.
    try {
      const list = await api.get("/customers?paginate=false");
      setCustomers(list.data);
    } catch (_) { /* ignore */ }
    if (created?.id) setForm(f => ({ ...f, customer_id: created.id }));
  };

  const load = () => {
    const params = new URLSearchParams({ page: String(page), page_size: "20" });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    api.get(`/invoices?${params}`).then(r => setData(r.data));
    api.get("/statements").then(r => setStatements(r.data)).catch(() => {});
    api.get("/payments").then(r => setPayments(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/customers?paginate=false").then(r => setCustomers(r.data));
    // Per-tenant Stripe must be configured (own sk_… key) — env fallback is NOT allowed for customer-facing payments.
    api.get("/company/settings").then(r => {
      const s = r.data.stripe || {};
      setStripeReady(Boolean(s.configured && !s.using_env_fallback));
    }).catch(() => setStripeReady(false));
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) {
      const poll = async (attempts = 0) => {
        try {
          const r = await api.get(`/payments/status/${sid}`);
          if (r.data.payment_status === "paid") { setPaymentStatus("Payment successful ✓"); load(); return; }
          if (attempts >= 8) { setPaymentStatus("Status pending — please refresh"); return; }
          setTimeout(() => poll(attempts + 1), 2000);
        } catch { setPaymentStatus("Payment status check failed"); }
      };
      Promise.resolve().then(() => setPaymentStatus("Checking payment..."));
      poll();
      window.history.replaceState({}, "", "/invoices");
    }
  }, []);
  useEffect(() => { load(); }, [page, status]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const addLine = () => setForm({ ...form, lines: [...form.lines, { description: "", quantity: "1", unit_price: "0" }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, field, val) => { const ls = [...form.lines]; ls[i] = { ...ls[i], [field]: val }; setForm({ ...form, lines: ls }); };
  const lineTotal = (l) => parseFloat(l.quantity || "0") * parseFloat(l.unit_price || "0");
  const subtotal = form.lines.reduce((a, l) => a + lineTotal(l), 0);

  const submit = async () => {
    const payload = {
      customer_id: form.customer_id || null,
      due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      tax: parseFloat(form.tax || "0"),
      notes: form.notes,
      lines: form.lines.map(l => ({ description: l.description, quantity: parseFloat(l.quantity || "1"), unit_price: parseFloat(l.unit_price || "0") })),
    };
    await api.post("/invoices", payload);
    setOpen(false);
    setForm({ customer_id: "", due_date: "", tax: "0", notes: "", lines: [{ description: "", quantity: "1", unit_price: "0" }] });
    load();
  };
  const send = async (id) => { await api.post(`/invoices/${id}/send`); load(); };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/invoices/${id}`); load(); } };
  const pay = async (inv) => {
    try {
      const r = await api.post("/payments/checkout", { invoice_id: inv.id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { alert(formatErr(e)); }
  };
  const downloadPdf = async (inv) => {
    const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const cName = (id) => customers.find(c => c.id === id)?.name || "—";

  return (
    <Page
      title="Receivables"
      subtitle={`FINANCE / ${view === "statements" ? "STATEMENTS" : view === "payments" ? "PAYMENTS" : "INVOICES"}`}
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Receivables"]}
      testid="invoices-page"
      actions={<>
        <div className="flex gap-1 mr-2" data-testid="receivables-view-toggle">
          <button
            className={`btn btn-sm ${view === "invoices" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setView("invoices")}
            data-testid="receivables-view-invoices"
          >INVOICES ({data.total})</button>
          <button
            className={`btn btn-sm ${view === "statements" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setView("statements")}
            data-testid="receivables-view-statements"
          >STATEMENTS ({statements.length})</button>
          <button
            className={`btn btn-sm ${view === "payments" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setView("payments")}
            data-testid="receivables-view-payments"
          >PAYMENTS ({payments.length})</button>
        </div>
        <button className="btn btn-primary" onClick={() => setOpen(true)} data-testid="new-invoice-btn"><Plus size={14} /> NEW INVOICE</button>
      </>}
    >

      {view === "payments" && (
        <div className="surface" data-testid="payments-panel">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <span className="heading">Payments Received</span>
            <span className="text-xs text-muted-foreground">Checks, ACH, manual cards, and Stripe payments</span>
          </div>
          {payments.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm" data-testid="empty-payments">
              No payments received yet. Once you record a check / mark an invoice paid / a Stripe checkout completes, payments will appear here.
            </div>
          ) : (
            <table className="dense">
              <thead><tr>
                <th>RECEIVED</th>
                <th>METHOD</th>
                <th>REFERENCE</th>
                <th>INVOICE</th>
                <th>CUSTOMER</th>
                <th className="num">AMOUNT</th>
                <th>SOURCE</th>
              </tr></thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                    <td className="text-xs">{p.received_at ? fmtDate(p.received_at) : "—"}</td>
                    <td className="text-xs mono uppercase" data-testid={`payment-method-${p.id}`}>{p.method || "—"}</td>
                    <td className="text-xs mono">{p.reference || "—"}</td>
                    <td className="text-xs mono">{p.invoice_number || "—"}</td>
                    <td className="text-xs">{p.customer_name || "—"}</td>
                    <td className="num">{fmtMoney(p.amount)}</td>
                    <td className="text-xs">
                      <span className={`badge ${p.source === "stripe" ? "badge-blue" : "badge-grey"}`}>
                        {p.source === "stripe" ? "STRIPE" : "MANUAL"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {view === "statements" && (
        <div className="surface" data-testid="statements-panel">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <span className="heading">Statements</span>
            <span className="text-xs text-muted-foreground">Monthly account statements sent to clients</span>
          </div>
          {statements.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm" data-testid="empty-statements">
              No statements yet. Once you generate monthly statements from grouped invoices, they&apos;ll appear here.
            </div>
          ) : (
            <table className="dense">
              <thead><tr><th>STMT #</th><th>ISSUED</th><th>DUE</th><th className="num">TOTAL</th><th className="num">BALANCE</th><th>STATUS</th><th></th></tr></thead>
              <tbody>
                {statements.map(s => (
                  <tr key={s.id} data-testid={`statement-row-${s.id}`}>
                    <td className="mono">{s.statement_number || s.id.slice(0, 8)}</td>
                    <td className="text-xs">{s.issued_at ? fmtDate(s.issued_at) : "—"}</td>
                    <td className="text-xs">{s.due_date ? fmtDate(s.due_date) : "—"}</td>
                    <td className="num">{fmtMoney(s.total_amount)}</td>
                    <td className="num">{fmtMoney(s.balance)}</td>
                    <td>{s.is_paid ? <span className="badge badge-green">PAID</span> : <span className="badge badge-yellow">OPEN</span>}</td>
                    <td className="text-right">
                      <a
                        className="btn btn-ghost btn-sm"
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/statements/${s.id}/pdf`}
                        target="_blank" rel="noreferrer"
                        data-testid={`statement-pdf-${s.id}`}
                      >PDF</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {view === "invoices" && (
        <>
          {paymentStatus && <div className="surface p-3 mb-4 mono text-sm" data-testid="payment-status">{paymentStatus}</div>}
          {!stripeReady && (
            <div className="surface p-4 mb-4 border-l-4" style={{ borderLeftColor: "#FACC15" }} data-testid="stripe-not-configured-banner">
              <div className="label-xs mb-1">CUSTOMER PAYMENTS DISABLED</div>
              <div className="text-sm">
                Add <strong>your own</strong> Stripe Secret Key in <a className="underline" href="/settings">Settings → Stripe Credentials</a> so invoice payments settle into your Stripe account, not {platformPossessive}.
              </div>
            </div>
          )}
          <ListControls
            testid="invoices-controls"
            q={q} onQ={setQ} placeholder="Search by number or notes..."
            filters={[{ key: "status", label: "STATUS", value: status, onChange: setStatus, options: STATUS_OPTS.invoice }]}
            pagination={data} total={data.total} onPageChange={setPage}
          />
          <div className="surface"><table className="dense">
            <thead><tr><th>NUMBER</th><th>CUSTOMER</th><th>ISSUED</th><th>DUE</th><th className="num">TOTAL</th><th className="num">PAID</th><th>STATUS</th><th></th></tr></thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan="8" className="text-center text-muted-foreground py-12">No invoices</td></tr>}
              {data.items.map(i => (
                <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                  <td className="mono">{i.invoice_number}</td>
                  <td>{cName(i.customer_id)}</td>
                  <td className="text-xs">{fmtDate(i.issue_date)}</td>
                  <td className="text-xs">{fmtDate(i.due_date)}</td>
                  <td className="num">{fmtMoney(i.total)}</td>
                  <td className="num text-green-600">{fmtMoney(i.amount_paid)}</td>
                  <td><StatusBadge status={i.status} /></td>
                  <td className="text-right whitespace-nowrap">
                    <button className="btn btn-ghost btn-sm" onClick={() => downloadPdf(i)} data-testid={`pdf-invoice-${i.id}`} title="Download PDF"><DownloadSimple size={12} /></button>
                    {i.status === "draft" && <button className="btn btn-ghost btn-sm" onClick={() => send(i.id)} data-testid={`send-invoice-${i.id}`}><PaperPlaneTilt size={12} /> SEND</button>}
                    {i.status === "sent" && <button className="btn btn-secondary btn-sm" onClick={() => pay(i)} disabled={!stripeReady} title={!stripeReady ? "Add your Stripe key in Settings to enable payments" : "Pay invoice"} data-testid={`pay-invoice-${i.id}`}><CreditCard size={12} /> PAY</button>}
                    <button className="btn btn-ghost btn-sm" onClick={() => del(i.id)}><Trash size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </>
      )}

      <Drawer open={open} onClose={() => setOpen(false)} title="New invoice" size="wide" testid="new-invoice-drawer"
        footer={<><button className="btn btn-secondary" onClick={() => setOpen(false)}>CANCEL</button><button className="btn btn-primary" onClick={submit} data-testid="save-invoice-btn">CREATE INVOICE</button></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CUSTOMER">
            <div className="flex gap-2">
              <select className="input flex-1" value={form.customer_id} onChange={e => setForm({ ...form, customer_id: e.target.value })} data-testid="invoice-customer-select"><option value="">—</option>{customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
              <button
                type="button"
                className="btn btn-secondary btn-sm whitespace-nowrap"
                onClick={() => { setAddCustOpen(true); }}
                data-testid="invoice-add-customer-btn"
                title="Create a new customer without leaving this form"
              >
                <Plus size={12} /> NEW
              </button>
            </div>
          </Field>
          <Field label="DUE DATE"><input className="input" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></Field>
        </div>
        <div className="label-xs mt-4 mb-2">LINE ITEMS</div>
        <div className="space-y-2">
          {form.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <input className="input col-span-6" placeholder="Description" value={l.description} onChange={e => updateLine(i, "description", e.target.value)} data-testid={`line-desc-${i}`} />
              <input className="input col-span-2 mono" type="number" step="0.01" placeholder="Qty" value={l.quantity} onChange={e => updateLine(i, "quantity", e.target.value)} />
              <input className="input col-span-3 mono" type="number" step="0.01" placeholder="Unit" value={l.unit_price} onChange={e => updateLine(i, "unit_price", e.target.value)} />
              <button className="btn btn-ghost btn-sm col-span-1" onClick={() => removeLine(i)}><Trash size={12} /></button>
            </div>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm mt-2" onClick={addLine} data-testid="add-line-btn"><Plus size={12} /> ADD LINE</button>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <Field label="TAX"><input className="input mono" type="number" step="0.01" value={form.tax} onChange={e => setForm({ ...form, tax: e.target.value })} /></Field>
          <Field label="SUBTOTAL"><div className="input mono bg-secondary">{fmtMoney(subtotal)}</div></Field>
        </div>
        <Field label="NOTES"><textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
      </Drawer>

      {/* Stacked child drawer: create a new customer without leaving the
          invoice form. Uses the reusable CustomerFormDrawer so the UX is
          identical to Organizations + Jobs. */}
      <CustomerFormDrawer
        open={addCustOpen}
        onClose={() => setAddCustOpen(false)}
        customer={null}
        subtitle="The new customer will be created and auto-selected on the invoice behind this panel."
        onSaved={onCustomerCreated}
      />
    </Page>
  );
}
